import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  CATALOG_THEME_PALETTES,
  getCatalogThemePalette,
  readStoredCatalogTheme,
  CATALOG_THEME_STORAGE_KEY,
} from '../../utils/catalogThemePalettes';
import {
  readLikedTrackIds,
  persistLikedTrackIds,
  downloadTracks,
} from '../../utils/catalogLikes';
import { useLocale } from '../../utils/catalogI18n';
import './SoundCatalog.css';

function HeartIcon({ filled = false }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {filled ? (
        <path
          fill="currentColor"
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        />
      ) : (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        />
      )}
    </svg>
  );
}

function LikeButton({ isLiked, trackName, onToggle }) {
  const t = useLocale();
  return (
    <button
      type="button"
      className={`playlist-like-btn${isLiked ? ' is-liked' : ''}`}
      aria-label={isLiked ? t.removeFromSelection(trackName) : t.addToSelection(trackName)}
      aria-pressed={isLiked}
      onClick={onToggle}
    >
      <HeartIcon filled={isLiked} />
    </button>
  );
}

function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true" focusable="false">
      <circle cx="9" cy="20" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="17" cy="20" r="1.5" fill="currentColor" stroke="none" />
      <path d="M3 4h2l2.2 11.2a2 2 0 002 1.8h7.6a2 2 0 001.9-1.5L20 8H7" strokeLinecap="square" strokeLinejoin="miter" />
    </svg>
  );
}

function VolumeIcon({ volume }) {
  const body = <path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3z" />;
  if (volume === 0) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        {body}
        <line x1="17" y1="9" x2="23" y2="15" />
        <line x1="23" y1="9" x2="17" y2="15" />
      </svg>
    );
  }
  if (volume <= 0.4) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        {body}
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
      {body}
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

/** Pixel backdrop-filter (url SVG) : Chrome / Safari desktop, Android… Pas iOS ni Firefox. */
function canUsePixelBackdropFilter() {
  if (typeof window === 'undefined' || typeof CSS === 'undefined' || !CSS.supports) {
    return false;
  }
  const blurOk =
    CSS.supports('backdrop-filter: blur(1px)') ||
    CSS.supports('-webkit-backdrop-filter: blur(1px)');
  if (!blurOk) return false;

  const urlOk =
    CSS.supports('backdrop-filter: url("#")') ||
    CSS.supports('-webkit-backdrop-filter: url("#")');
  if (!urlOk) return false;

  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return false;

  return true;
}

const CATEGORY_KEYWORDS = [
  { variants: ['HIP HOP', 'HIPHOP'], display: 'HIP HOP' },
  { variants: ['TRIP HOP', 'TRIPHOP'], display: 'TRIP HOP' },
  { variants: ['DRUM N BASS', 'DNB'], display: 'DRUM N BASS' },
  { variants: ['JAZZ'], display: 'JAZZ' },
  { variants: ['AMBIENT'], display: 'AMBIENT' },
  { variants: ['ROCK'], display: 'ROCK' },
  { variants: ['BOOMBAP'], display: 'BOOMBAP' },
  { variants: ['LOFI'], display: 'LOFI' },
  { variants: ['ELECTRONIC', 'ELECTRO'], display: 'ELECTRONIC' },
  { variants: ['SYNTH'], display: 'SYNTH' },
  { variants: ['FUNK'], display: 'FUNK' },
  { variants: ['SOUL'], display: 'SOUL' },
  { variants: ['TRAP'], display: 'TRAP' },
  { variants: ['GLITCH'], display: 'GLITCH' },
  { variants: ['EXPERIMENTAL'], display: 'EXPERIMENTAL' },
  { variants: ['POP'], display: 'POP' },
  { variants: ['HOUSE'], display: 'HOUSE' },
  { variants: ['ACID'], display: 'ACID' },
  { variants: ['BREAKBEAT'], display: 'BREAKBEAT' },
  { variants: ['TECHNO'], display: 'TECHNO' },
  { variants: ['DUB'], display: 'DUB' },
  { variants: ['INDUS'], display: 'INDUS' },
  { variants: ['SHOEGAZE'], display: 'SHOEGAZE' },
  { variants: ['REGGAE'], display: 'REGGAE' },
  { variants: ['AFRO'], display: 'AFRO' },
  { variants: ['IDM'], display: 'IDM' },
  { variants: ['RAVE'], display: 'RAVE' },
  { variants: ['ABSTRACT'], display: 'ABSTRACT' },
];

function extractNameCategories(tracks) {
  const found = new Set();
  tracks.forEach((track) => {
    const name = (track.name || '').toUpperCase();
    CATEGORY_KEYWORDS.forEach(({ variants, display }) => {
      if (variants.some((v) => name.includes(v))) found.add(display);
    });
  });
  return [...found].sort();
}

function extractStyleCategories(tracks) {
  const found = new Set();
  tracks.forEach((track) => {
    const styles = track.styles || '';
    if (!styles || styles === '—') return;
    styles.split('/').map((s) => s.trim()).filter(Boolean).forEach((s) => found.add(s));
  });
  return [...found].sort((a, b) => a.localeCompare(b, 'fr'));
}

function formatTime(seconds) {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function splitStyles(styles) {
  if (!styles || styles === '—') return [];
  return styles.split('/').map((s) => s.trim()).filter(Boolean);
}

const COLLAB_NEVER_DISPLAY = new Set([
  'fsq',
  'foreign sequence',
  'foreign seq',
  'clean',
  'dirty',
  'taperecorded',
  'tape recorded',
  'tape-recorded',
]);

function isDisplayCollaborator(name) {
  const key = String(name).toLowerCase().replace(/\s+/g, ' ').trim();
  if (!key) return false;
  if (COLLAB_NEVER_DISPLAY.has(key)) return false;
  if (/^taperecord/i.test(key)) return false;
  return true;
}

function splitCollaborators(collaborators) {
  if (!collaborators || collaborators === '—') return [];
  return collaborators
    .split(/\s*·\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter(isDisplayCollaborator);
}

const MAIN_PROJECTS = new Set([
  '2 Girls Radio', 'Bad Actors', 'Rachim Xp', 'Nus De Dos', 'Foreign Sequence', 'The Meeting', 'Tonotopy',
]);

const SoundCatalog = ({
  tracks,
  getTrackUrl,
  title = 'SOUND CATALOG',
  infoTooltip = "Musiques originales pour vos projets de synchro et publicité.",
  mode = 'popup',
  onClose,
  metadataMode = 'none',
  autoPlayFromUrl = false,
  accessLabel,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showInfoTip, setShowInfoTip] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showLikesCart, setShowLikesCart] = useState(false);
  const [likedTrackIds, setLikedTrackIds] = useState(() => readLikedTrackIds());
  const [isDownloadingLikes, setIsDownloadingLikes] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [themeId, setThemeId] = useState(readStoredCatalogTheme);

  const likesEnabled = metadataMode === 'full';

  const themePalette = useMemo(() => getCatalogThemePalette(themeId), [themeId]);
  const themeStyle = useMemo(() => ({ ...themePalette.vars }), [themePalette]);
  const backdropUsesBlurFallback = useMemo(() => !canUsePixelBackdropFilter(), []);
  const t = useLocale();

  const audioRef = useRef(null);
  const audioInstancesRef = useRef(new Map());
  const urlTrackLoadedRef = useRef(false);
  const playTimeoutRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const playingItemRef = useRef(null);
  const filteredTracksRef = useRef([]);
  const playTrackRef = useRef(() => {});
  const isSeekingRef = useRef(false);

  const categoryVariantsMap = useMemo(() => {
    const map = new Map();
    CATEGORY_KEYWORDS.forEach(({ variants, display }) => map.set(display, variants));
    return map;
  }, []);

  const categories = useMemo(
    () => (metadataMode === 'full' ? extractStyleCategories(tracks) : extractNameCategories(tracks)),
    [tracks, metadataMode]
  );

  const projects = useMemo(() => {
    if (metadataMode !== 'full') return [];
    const all = [...new Set(tracks.map((t) => t.project).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, 'fr')
    );
    const main = all.filter((p) => MAIN_PROJECTS.has(p));
    const back = all.filter((p) => !MAIN_PROJECTS.has(p));
    return { main, back };
  }, [tracks, metadataMode]);

  const playbackVolume = isMobileViewport ? 1 : volume;

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => {
      setIsMobileViewport(mq.matches);
      if (mq.matches) setShowVolumeSlider(false);
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!showInfoTip) return undefined;
    const close = (e) => {
      if (!e.target.closest('.sound-catalog-info-wrap')) setShowInfoTip(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showInfoTip]);

  useEffect(() => {
    try {
      localStorage.setItem(CATALOG_THEME_STORAGE_KEY, themeId);
    } catch {
      /* ignore */
    }
  }, [themeId]);

  useEffect(() => {
    if (!showThemePicker) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowThemePicker(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showThemePicker]);

  useEffect(() => {
    if (!likesEnabled) return undefined;
    persistLikedTrackIds(likedTrackIds);
  }, [likedTrackIds, likesEnabled]);

  useEffect(() => {
    if (!showLikesCart) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowLikesCart(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showLikesCart]);

  const likedTracks = useMemo(() => {
    if (!likesEnabled) return [];
    const idSet = new Set(likedTrackIds);
    return tracks.filter((track) => idSet.has(track.id));
  }, [tracks, likedTrackIds, likesEnabled]);

  const toggleLike = useCallback((trackId, e) => {
    e?.stopPropagation();
    setLikedTrackIds((prev) => (
      prev.includes(trackId) ? prev.filter((id) => id !== trackId) : [...prev, trackId]
    ));
    setDownloadError('');
  }, []);

  const removeLike = useCallback((trackId) => {
    setLikedTrackIds((prev) => prev.filter((id) => id !== trackId));
    setDownloadError('');
  }, []);

  const handleDownloadLikes = useCallback(async () => {
    if (!likedTracks.length || isDownloadingLikes) return;
    setIsDownloadingLikes(true);
    setDownloadError('');
    try {
      await downloadTracks(likedTracks, getTrackUrl);
    } catch (err) {
      setDownloadError(t.downloadError);
    } finally {
      setIsDownloadingLikes(false);
    }
  }, [likedTracks, getTrackUrl, isDownloadingLikes, t]);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
      } catch { /* ignore */ }
      audioRef.current = null;
    }
    audioInstancesRef.current.forEach((audio) => {
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      } catch { /* ignore */ }
    });
    audioInstancesRef.current.clear();
    setIsPlaying(false);
  }, []);

  const stopAllAudio = useCallback(() => {
    stopPlayback();
    setCurrentlyPlaying(null);
    setCurrentTime(0);
    setDuration(0);
  }, [stopPlayback]);

  const playTrack = useCallback((track) => {
    if (!track) return;
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
      playTimeoutRef.current = null;
    }
    setCurrentlyPlaying(track.id);
    setCurrentTime(0);
    setDuration(0);
    stopPlayback();
    playTimeoutRef.current = setTimeout(() => {
      playTimeoutRef.current = null;
      const audio = new Audio(getTrackUrl(track));
      audio.volume = playbackVolume;
      audioRef.current = audio;
      audioInstancesRef.current.set(track.id, audio);
      audio.addEventListener('ended', () => {
        const list = filteredTracksRef.current;
        const idx = list.findIndex((t) => t.id === track.id);
        if (idx >= 0 && idx < list.length - 1) {
          playTrackRef.current(list[idx + 1]);
          return;
        }
        setCurrentlyPlaying(null);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        audioRef.current = null;
      });
      const syncDuration = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
      };
      const syncCurrentTime = () => {
        if (!isSeekingRef.current) setCurrentTime(audio.currentTime);
      };
      audio.addEventListener('loadedmetadata', syncDuration);
      audio.addEventListener('durationchange', syncDuration);
      audio.addEventListener('timeupdate', syncCurrentTime);
      audio.addEventListener('play', () => setIsPlaying(true));
      audio.addEventListener('pause', () => setIsPlaying(false));
      syncDuration();
      audio.play().catch((err) => console.error('Erreur lecture:', err));
    }, 80);
  }, [getTrackUrl, stopPlayback, playbackVolume]);

  const filteredTracks = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return tracks.filter((track) => {
      const haystack = [track.name, track.project, track.styles]
        .filter(Boolean).join(' ').toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      const matchesProject =
        metadataMode !== 'full' || selectedProject === 'all' || track.project === selectedProject;
      if (selectedCategory === 'all') return matchesSearch && matchesProject;
      if (metadataMode === 'full') {
        const styles = (track.styles || '').split('/').map((s) => s.trim());
        return matchesSearch && matchesProject && styles.includes(selectedCategory);
      }
      const variants = categoryVariantsMap.get(selectedCategory) || [selectedCategory];
      const nameUp = (track.name || '').toUpperCase();
      return matchesSearch && matchesProject && variants.some((v) => nameUp.includes(v));
    });
  }, [tracks, searchQuery, selectedCategory, selectedProject, metadataMode, categoryVariantsMap]);

  filteredTracksRef.current = filteredTracks;
  playTrackRef.current = playTrack;
  isSeekingRef.current = isSeeking;

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = playbackVolume;
  }, [playbackVolume]);

  useEffect(() => {
    if (!autoPlayFromUrl) return undefined;
    const trackId = parseInt(new URLSearchParams(window.location.search).get('track'), 10);
    if (!Number.isFinite(trackId) || trackId <= 0 || !tracks.length || urlTrackLoadedRef.current) {
      return undefined;
    }
    const track = tracks.find((t) => t.id === trackId);
    if (track) {
      urlTrackLoadedRef.current = true;
      playTrack(track);
    }
    return undefined;
  }, [autoPlayFromUrl, tracks, playTrack]);

  useEffect(() => () => {
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
      playTimeoutRef.current = null;
    }
    stopAllAudio();
  }, [stopAllAudio]);

  const scrollToPlayingTrack = useCallback(() => {
    const container = scrollAreaRef.current;
    const item = playingItemRef.current;
    if (!container || !item) return;
    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const isVisible =
      itemRect.top >= containerRect.top && itemRect.bottom <= containerRect.bottom;
    if (isVisible) return;
    item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!currentlyPlaying) return undefined;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToPlayingTrack);
    });
    return () => cancelAnimationFrame(frame);
  }, [currentlyPlaying, scrollToPlayingTrack]);

  const currentTrack = tracks.find((t) => t.id === currentlyPlaying);
  const currentTrackCollabs =
    currentTrack && metadataMode === 'full'
      ? splitCollaborators(currentTrack.collaborators)
      : [];
  const seekFillPercent =
    duration > 0
      ? `${Math.min(100, Math.max(0, (currentTime / duration) * 100))}%`
      : '0%';

  const usePageNavHead = mode === 'page' || Boolean(accessLabel) || likesEnabled;

  const toggleThemePicker = useCallback((e) => {
    e.stopPropagation();
    setShowInfoTip(false);
    setShowLikesCart(false);
    setShowThemePicker((v) => !v);
  }, []);

  const toggleLikesCart = useCallback((e) => {
    e.stopPropagation();
    setShowInfoTip(false);
    setShowThemePicker(false);
    setShowLikesCart((v) => !v);
  }, []);

  const themePicker = showThemePicker && (
    <div className="sound-catalog-theme-popover" role="dialog" aria-label={t.colorPalettes}>
      <p className="sound-catalog-theme-popover-label">{t.palette}</p>
      <div className="sound-catalog-theme-swatches">
        {CATALOG_THEME_PALETTES.map((palette) => (
          <button
            key={palette.id}
            type="button"
            className={`sound-catalog-theme-swatch${themeId === palette.id ? ' is-active' : ''}`}
            style={{ '--theme-swatch-color': palette.swatch }}
            onClick={(e) => {
              e.stopPropagation();
              setThemeId(palette.id);
              setShowThemePicker(false);
            }}
            aria-label={palette.label}
            aria-pressed={themeId === palette.id}
            title={palette.label}
          />
        ))}
      </div>
    </div>
  );

  const headTitle = (
    <div className="sound-catalog-head-title-wrap">
      <button
        type="button"
        className="sound-catalog-head-title-trigger"
        aria-label={t.choosePalette}
        aria-expanded={showThemePicker}
        onClick={toggleThemePicker}
      />
      <h2 className="sound-catalog-head-title">{title}</h2>
      {themePicker}
    </div>
  );

  const renderLikesCartButton = (headPlacement = false) => likesEnabled && (
    <span className={`sound-catalog-likes-wrap${headPlacement ? ' sound-catalog-likes-wrap--head' : ''}`}>
      <button
        type="button"
        className={`sound-catalog-likes-btn${showLikesCart ? ' is-active' : ''}${headPlacement ? ' sound-catalog-likes-btn--head' : ''}`}
        aria-label={t.mySelection}
        aria-expanded={showLikesCart}
        onClick={toggleLikesCart}
      >
        <HeartIcon filled={showLikesCart} />
        {likedTracks.length > 0 && (
          <span className="sound-catalog-likes-badge" aria-hidden="true">
            {likedTracks.length}
          </span>
        )}
      </button>
      {showLikesCart && (
        <div className="sound-catalog-likes-cart" role="dialog" aria-label={t.likedTracksDialog}>
          <p className="sound-catalog-likes-cart-title">{t.mySelection}</p>
          {likedTracks.length === 0 ? (
            <p className="sound-catalog-likes-cart-empty">
              {t.emptyCart}
            </p>
          ) : (
            <ul className="sound-catalog-likes-cart-list">
              {likedTracks.map((track) => (
                <li key={track.id} className="sound-catalog-likes-cart-item">
                  <button
                    type="button"
                    className="sound-catalog-likes-cart-play"
                    onClick={() => {
                      playTrack(track);
                      setShowLikesCart(false);
                    }}
                  >
                    <span className="sound-catalog-likes-cart-name">{track.name}</span>
                    {track.project && (
                      <span className="sound-catalog-likes-cart-project">{track.project}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="sound-catalog-likes-cart-remove"
                    aria-label={t.removeTrack(track.name)}
                    onClick={() => removeLike(track.id)}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          {downloadError && (
            <p className="sound-catalog-likes-cart-error" role="alert">{downloadError}</p>
          )}
          <button
            type="button"
            className="sound-catalog-likes-download-btn"
            disabled={!likedTracks.length || isDownloadingLikes}
            onClick={handleDownloadLikes}
          >
            {isDownloadingLikes
              ? t.downloading
              : likedTracks.length ? t.downloadCount(likedTracks.length) : t.download}
          </button>
        </div>
      )}
    </span>
  );

  const infoButton = (
    <span className="sound-catalog-info-wrap">
      <button
        type="button"
        className={`sound-catalog-info${showInfoTip ? ' is-active' : ''}`}
        aria-label={t.catalogInfo}
        aria-expanded={showInfoTip}
        onClick={(e) => {
          e.stopPropagation();
          setShowInfoTip((v) => !v);
        }}
      >
        ?
      </button>
      {showInfoTip && (
        <div className="sound-catalog-info-popover" role="tooltip">
          {infoTooltip}
        </div>
      )}
    </span>
  );

  const closeChrome = () => {
    setShowInfoTip(false);
    setShowThemePicker(false);
    setShowLikesCart(false);
  };

  const headChromeOpen = showInfoTip || showThemePicker || showLikesCart;

  const catalogContent = (
    <div
      className={`sound-catalog-card ${mode === 'page' ? 'sound-catalog-card--page' : ''}${headChromeOpen ? ' sound-catalog-card--chrome-open' : ''}`}
      onClick={(e) => mode === 'popup' && e.stopPropagation()}
    >
      {headChromeOpen && (
        <button
          type="button"
          className={`sound-catalog-info-backdrop${backdropUsesBlurFallback ? ' sound-catalog-info-backdrop--blur-fallback' : ''}`}
          aria-label={t.close}
          onClick={closeChrome}
        />
      )}
      <svg className="sound-catalog-filters" aria-hidden="true" focusable="false">
        <defs>
          <filter
            id="sound-catalog-pixelate"
            x="0"
            y="0"
            width="100%"
            height="100%"
            colorInterpolationFilters="sRGB"
          >
            <feFlood x="11" y="11" height="1" width="1" />
            <feComposite width="22" height="22" />
            <feTile result="sound-catalog-pixel-tile" />
            <feComposite in="SourceGraphic" in2="sound-catalog-pixel-tile" operator="in" result="sound-catalog-pixel-sampled" />
            <feMorphology in="sound-catalog-pixel-sampled" operator="dilate" radius="11" result="sound-catalog-pixel-blocks" />
            <feColorMatrix
              in="sound-catalog-pixel-blocks"
              type="matrix"
              values="0.92 0.04 0.04 0 0.03
                      0.04 0.90 0.06 0 0.03
                      0.04 0.05 0.91 0 0.03
                      0    0    0    1 0"
            />
          </filter>
          <filter
            id="sound-catalog-pixelate-mobile"
            x="0"
            y="0"
            width="100%"
            height="100%"
            colorInterpolationFilters="sRGB"
          >
            <feFlood x="8" y="8" height="1" width="1" />
            <feComposite width="16" height="16" />
            <feTile result="sound-catalog-pixel-tile-mobile" />
            <feComposite in="SourceGraphic" in2="sound-catalog-pixel-tile-mobile" operator="in" result="sound-catalog-pixel-sampled-mobile" />
            <feMorphology in="sound-catalog-pixel-sampled-mobile" operator="dilate" radius="8" result="sound-catalog-pixel-blocks-mobile" />
            <feColorMatrix
              in="sound-catalog-pixel-blocks-mobile"
              type="matrix"
              values="0.92 0.04 0.04 0 0.03
                      0.04 0.90 0.06 0 0.03
                      0.04 0.05 0.91 0 0.03
                      0    0    0    1 0"
            />
          </filter>
        </defs>
      </svg>
      <div
        className={`sound-catalog-head${usePageNavHead ? ' sound-catalog-head--page-nav' : ''}`}
      >
        {usePageNavHead ? (
          <>
            <div className="sound-catalog-head-side sound-catalog-head-side--left">
              {renderLikesCartButton(true)}
            </div>
            <div className="sound-catalog-head-center">
              {headTitle}
              {accessLabel && <p className="sound-catalog-head-access">{accessLabel}</p>}
            </div>
            <div className="sound-catalog-head-side sound-catalog-head-side--right">
              {infoButton}
            </div>
          </>
        ) : (
          <>
            <div className="sound-catalog-head-title-row">
              {headTitle}
              <div className="sound-catalog-head-actions">
                {renderLikesCartButton()}
                {infoButton}
              </div>
            </div>
            {mode === 'popup' && onClose && (
              <button type="button" className="neo-btn neo-btn-close" onClick={onClose} aria-label={t.close}>
                ✕
              </button>
            )}
          </>
        )}
      </div>
      <div className="sound-catalog-content">
        <div className="sound-catalog-toolbar">
          <input
            type="text"
            className="neo-input search-input"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value) {
                setSelectedProject('all');
                setSelectedCategory('all');
              }
            }}
          />
          {metadataMode === 'full' && (
            <select
              className="neo-select catalog-filter-select catalog-filter-select--project"
              value={selectedProject}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedProject(value);
                if (value !== 'all') setSelectedCategory('all');
                e.target.blur();
              }}
              aria-label={t.filterByProject}
            >
              <option value="all">{t.allProjects}</option>
              {projects.main.length > 0 && (
                <optgroup label={t.mainProjects}>
                  {projects.main.map((p) => <option key={p} value={p}>{p}</option>)}
                </optgroup>
              )}
              {projects.back.length > 0 && (
                <optgroup label={t.backCatalog}>
                  {projects.back.map((p) => <option key={p} value={p}>{p}</option>)}
                </optgroup>
              )}
            </select>
          )}
          {categories.length > 0 && (
            <select
              className="neo-select catalog-filter-select catalog-filter-select--style"
              value={selectedCategory}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCategory(value);
                if (value !== 'all') setSelectedProject('all');
                e.target.blur();
              }}
              aria-label={t.filterByStyle}
            >
              <option value="all">{t.allStyles}</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
        </div>
        <div className="sound-catalog-scroll-area" ref={scrollAreaRef}>
          <div className="sound-catalog-playlist">
            <p className="sound-catalog-count" role="status" aria-live="polite">
              {t.results(filteredTracks.length)}
            </p>
            {filteredTracks.length === 0 ? (
              <div className="no-results"><p>{t.noResults}</p></div>
            ) : (
              filteredTracks.map((track) => {
                const trackCollabs =
                  metadataMode === 'full' ? splitCollaborators(track.collaborators) : [];
                const isLiked = likesEnabled && likedTrackIds.includes(track.id);
                return (
                <div
                  key={track.id}
                  ref={currentlyPlaying === track.id ? playingItemRef : null}
                  role="button"
                  tabIndex={0}
                  className={`playlist-item ${currentlyPlaying === track.id ? 'playing' : ''}`}
                  onClick={() => {
                    if (currentlyPlaying === track.id) stopAllAudio();
                    else playTrack(track);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (currentlyPlaying === track.id) stopAllAudio();
                      else playTrack(track);
                    }
                  }}
                >
                  <div className="track-details">
                    <div className="track-title-line">
                      <div className="track-title-main">
                        <h4>{track.name}</h4>
                        {likesEnabled && (
                          <LikeButton
                            isLiked={isLiked}
                            trackName={track.name}
                            onToggle={(e) => toggleLike(track.id, e)}
                          />
                        )}
                      </div>
                      {trackCollabs.length > 0 && (
                        <span className="track-title-collabs">
                          {trackCollabs.map((collab) => (
                            <span key={collab} className="track-title-collab">{collab}</span>
                          ))}
                        </span>
                      )}
                    </div>
                    {metadataMode === 'full' && (
                      <p className="track-meta">
                        {track.project && (
                          <span className="track-meta-badge track-meta-badge--project">{track.project}</span>
                        )}
                        {splitStyles(track.styles).map((style) => (
                          <span key={style} className="track-meta-badge track-meta-badge--style">{style}</span>
                        ))}
                      </p>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
        {currentTrack && (
        <div className="sound-catalog-player">
          <div className="sound-catalog-player-head">
            <span className="sc-player-title-row">
              <span className="sc-player-title-text">{currentTrack.name}</span>
              {likesEnabled && (
                <LikeButton
                  isLiked={likedTrackIds.includes(currentTrack.id)}
                  trackName={currentTrack.name}
                  onToggle={(e) => toggleLike(currentTrack.id, e)}
                />
              )}
            </span>
          </div>
          <div className="sc-player-body">
              <div className="sc-player">
                {metadataMode === 'full' &&
                  (currentTrack.project || currentTrackCollabs.length > 0) && (
                  <div className="sc-player-meta">
                    {currentTrack.project && (
                      <span className="sc-player-subtitle">{currentTrack.project}</span>
                    )}
                    {currentTrackCollabs.map((collab) => (
                      <span key={collab} className="track-meta-badge track-meta-badge--collab">{collab}</span>
                    ))}
                  </div>
                )}

                <div className="sc-player-seek">
                  <span className="sc-player-time">{formatTime(currentTime)}</span>
                  <div className="sc-player-scrub">
                    <input
                      type="range"
                      min="0"
                      max={duration > 0 ? duration : 1}
                      value={Math.min(currentTime, duration > 0 ? duration : 1)}
                      step="0.1"
                      disabled={!duration}
                      className="sc-player-range sc-player-range--progress"
                      style={{ '--seek-fill': seekFillPercent }}
                      onChange={(e) => {
                        const t = parseFloat(e.target.value);
                        setCurrentTime(t);
                        if (audioRef.current) audioRef.current.currentTime = t;
                      }}
                      onTouchStart={() => setIsSeeking(true)}
                      onTouchEnd={() => setIsSeeking(false)}
                      onMouseDown={() => setIsSeeking(true)}
                      onMouseUp={() => setIsSeeking(false)}
                    />
                  </div>
                  <span className="sc-player-time sc-player-time--end">
                    -{duration ? formatTime(Math.max(0, duration - currentTime)) : '0:00'}
                  </span>
                </div>

                <div className="sc-player-controls">
                  {!isMobileViewport && <div className="sc-player-controls-spacer" aria-hidden="true" />}
                  <div className="sc-player-transport">
                    <button
                      type="button"
                      className="neo-btn neo-btn-control"
                      onClick={(e) => {
                        const idx = filteredTracks.findIndex((t) => t.id === currentlyPlaying);
                        if (idx > 0) playTrack(filteredTracks[idx - 1]);
                        e.currentTarget.blur();
                      }}
                      aria-label={t.prev}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="neo-btn neo-btn-play"
                      onClick={(e) => {
                        if (!audioRef.current) return;
                        if (audioRef.current.paused) audioRef.current.play();
                        else audioRef.current.pause();
                        e.currentTarget.blur();
                      }}
                      aria-label={isPlaying ? t.pause : t.play}
                    >
                      {isPlaying ? (
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                      )}
                    </button>
                    <button
                      type="button"
                      className="neo-btn neo-btn-control"
                      onClick={(e) => {
                        const idx = filteredTracks.findIndex((t) => t.id === currentlyPlaying);
                        if (idx >= 0 && idx < filteredTracks.length - 1) playTrack(filteredTracks[idx + 1]);
                        e.currentTarget.blur();
                      }}
                      aria-label={t.next}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                  {!isMobileViewport && (
                    <div className="sc-player-volume-wrap">
                      <button
                        type="button"
                        className={`neo-btn neo-btn-icon sc-player-volume-btn${showVolumeSlider ? ' is-active' : ''}`}
                        onClick={(e) => {
                          setShowVolumeSlider((v) => !v);
                          e.currentTarget.blur();
                        }}
                        aria-label={t.volume}
                        aria-expanded={showVolumeSlider}
                      >
                        <VolumeIcon volume={volume} />
                      </button>
                      <div
                        className={`sc-player-volume-popover${showVolumeSlider ? ' is-visible' : ''}`}
                        role="group"
                        aria-label={t.volumeControl}
                      >
                        <div className="sc-player-volume-popover-inner">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={volume}
                            className="sc-player-range sc-player-range--volume"
                            onChange={(e) => setVolume(parseFloat(e.target.value))}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );

  if (mode === 'page') {
    return (
      <div className="sound-catalog-page-shell" style={themeStyle}>
        <div className="sound-catalog-page-inset">{catalogContent}</div>
      </div>
    );
  }

  return (
    <div className="sound-catalog-popup-overlay" style={themeStyle} onClick={() => onClose?.()}>
      {catalogContent}
    </div>
  );
};

export default SoundCatalog;
